// src/components/tutorials/JS_Function_Arrows.jsx
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Function_Arrows() {
  const refs = {
    basic: useRef(null),
    implicitReturn: useRef(null),
    withThis: useRef(null),
    noThis: useRef(null),
    concise: useRef(null),
  };

  const scrollTo = (key) =>
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const keyPoints = [
    { key: "basic", label: "Basic Arrow Function" },
    { key: "implicitReturn", label: "Implicit Return" },
    { key: "withThis", label: "Arrow Function with this" },
    { key: "noThis", label: "Arrow Function lacks this" },
    { key: "concise", label: "Concise vs Block Body" },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">⚡ JavaScript Arrow Functions (ES6)</h1>
      <p className="tutorial-intro">
        Arrow functions, introduced in ES6, provide a concise syntax for writing
        functions and handle the <code className="highlight_code">this</code>{" "}
        keyword differently from regular functions.
      </p>

      {/* Key Points Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1.5rem 2.5rem",
          borderRadius: "12px",
          marginTop: "1rem",
          marginBottom: "3rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "0.5rem",
          }}
        >
          {keyPoints.map((item) => (
            <p key={item.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(item.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  textDecoration: "underline",
                  color: "black",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {item.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1. Basic Arrow Function */}
      <div ref={refs.basic} className="tutorial-section">
        <h3>1️⃣ Basic Arrow Function</h3>
        <p>
          The simplest form of an arrow function uses the{" "}
          <code className="highlight_code">=&gt;</code> syntax.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const greet = (name) => {
  return "Hello " + name;
};

console.log(greet("Alice")); // Hello Alice`}
          />
          <TryItButton
            code={`const greet=(name)=>{return "Hello "+name;};console.log(greet("Alice"));`}
          />
        </div>
        <OutputBox>Output: Hello Alice</OutputBox>
      </div>

      {/* 2. Implicit Return */}
      <div ref={refs.implicitReturn} className="tutorial-section">
        <h3>2️⃣ Implicit Return</h3>
        <p>
          If the function body has only one expression, the{" "}
          <code className="highlight_code">return</code> keyword and braces can
          be omitted.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const square = n => n * n;

console.log(square(4)); // 16`}
          />
          <TryItButton code={`const square=n=>n*n;console.log(square(4));`} />
        </div>
        <OutputBox>Output: 16</OutputBox>
      </div>

      {/* 3. Arrow Function with this */}
      <div ref={refs.withThis} className="tutorial-section">
        <h3>
          3️⃣ Arrow Function with <code className="highlight_code">this</code>
        </h3>
        <p>
          Arrow functions capture <code className="highlight_code">this</code>{" "}
          from their outer scope.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`function Timer() {
  this.seconds = 0;
  setInterval(() => {
    this.seconds++;
    if (this.seconds <= 3) console.log(this.seconds);
  }, 1000);
}

new Timer();`}
          />
          <TryItButton
            code={`function Timer(){this.seconds=0;setInterval(()=>{this.seconds++;if(this.seconds<=3)console.log(this.seconds);},1000);}new Timer();`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            1
            <br />
            2
            <br />3
          </pre>
        </OutputBox>
      </div>

      {/* 4. Arrow Function lacks its own this */}
      <div ref={refs.noThis} className="tutorial-section">
        <h3>
          4️⃣ Arrow Functions Do Not Bind{" "}
          <code className="highlight_code">this</code>
        </h3>
        <p>
          Regular functions get their own{" "}
          <code className="highlight_code">this</code>, while arrow functions
          inherit from the surrounding context.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const obj = {
  name: "Bob",
  greet: () => console.log("Hello " + this.name)
};

obj.greet(); // Hello undefined`}
          />
          <TryItButton
            code={`const obj={name:"Bob",greet:()=>console.log("Hello "+this.name)};obj.greet();`}
          />
        </div>
        <OutputBox>Output: Hello undefined</OutputBox>
      </div>

      {/* 5. Concise vs Block Body */}
      <div ref={refs.concise} className="tutorial-section">
        <h3>5️⃣ Concise vs Block Body</h3>
        <p>
          Arrow functions can use concise (single-line) or block (multi-line)
          syntax.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`// Concise body
const add = (a, b) => a + b;

// Block body
const multiply = (a, b) => {
  const result = a * b;
  return result;
};

console.log(add(5, 3));      // 8
console.log(multiply(5, 3)); // 15`}
          />
          <TryItButton
            code={`const add=(a,b)=>a+b;const multiply=(a,b)=>{const result=a*b;return result;};console.log(add(5,3));console.log(multiply(5,3));`}
          />
        </div>
        <OutputBox>
          Output:
          <pre>
            8
            <br />
            15
          </pre>
        </OutputBox>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#0369a1" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn how arrow functions handle{" "}
            <code className="highlight_code">this</code> differently.
          </li>
          <li>Compare with regular functions in object methods.</li>
          <li>
            Use arrow functions in array methods like map, filter, reduce.
          </li>
        </ul>
      </div>
    </div>
  );
}
